import { z } from "zod";

import { INTERNATIONAL, US_CITIZEN } from "@equidefi/constants/profile";
import { ASCII_ONLY_REGEX } from "@equidefi/constants/validation";
import { postalCodeZodRefinement } from "@equidefi/portals/helpers/postalCode";

import { MESSAGE } from "@/constants/forms";

const string = (message: string) => z.string().trim().min(1, { message: message });
const asciiString = (message: string) =>
  string(message).refine((val) => ASCII_ONLY_REGEX.test(val), {
    message: MESSAGE.ASCII_ONLY,
  });
const asciiStringOptional = () =>
  z
    .string()
    .optional()
    .nullable()
    .refine((val) => !val || ASCII_ONLY_REGEX.test(val), {
      message: MESSAGE.ASCII_ONLY,
    });

const baseSchema = z.object({
  first_name: asciiString(MESSAGE.REQUIRED("First Name")),
  middle_name: asciiStringOptional(),
  last_name: asciiString(MESSAGE.REQUIRED("Last Name")),
  phone_number: string(MESSAGE.REQUIRED("Phone Number")),
});

// Personal + address info, editable for every existing signer (primary, joint, additional).
const piiSchema = z.object({
  birthdate: string(MESSAGE.REQUIRED("Date of Birth")),
  address_street_1: string(MESSAGE.REQUIRED("Street")),
  address_street_2: z.string().optional().nullable(),
  address_city: string(MESSAGE.REQUIRED("City")),
  address_state: string(MESSAGE.REQUIRED("State")),
  address_postal_code: z.string().trim().optional().nullable(),
  address_country_code: string(MESSAGE.REQUIRED("Country")).max(2, {
    message: "Must be 2 letters",
  }),
});

// Same fields as piiSchema but optional — additional signers may be saved without full PII.
const piiOptionalSchema = z.object({
  birthdate: z.string().optional().nullable(),
  address_street_1: z.string().optional().nullable(),
  address_street_2: z.string().optional().nullable(),
  address_city: z.string().optional().nullable(),
  address_state: z.string().optional().nullable(),
  address_postal_code: z.string().optional().nullable(),
  address_country_code: z.string().max(2, { message: "Must be 2 letters" }).optional().nullable(),
});

const entitySignerSchema = z.object({
  is_kyc: z.boolean({
    required_error: MESSAGE.REQUIRED("Is beneficial owner"),
  }),
  is_signer: z.boolean({
    required_error: MESSAGE.REQUIRED("Is signer"),
  }),
});

const addSignerSchema = z.object({
  email: z.string({ message: MESSAGE.REQUIRED("Email") }).email(),
});

const uboRefine = (schema: z.ZodTypeAny) =>
  schema.superRefine((data, ctx) => {
    if (!data.is_kyc && !data.is_signer) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Added person must be either a UBO or a signer.",
        path: ["is_signer"],
      });
    }
  });

export const buildSchema = (isPrimary: boolean, isNewSigner: boolean, isEntityNonPrimary: boolean) => {
  // PII is required for the primary signer, optional for additional signers.
  let schema = baseSchema.merge(isPrimary ? piiSchema : piiOptionalSchema);

  if (isNewSigner) {
    schema = schema.merge(addSignerSchema);
  }
  if (isEntityNonPrimary) {
    schema = schema.merge(entitySignerSchema);
  }

  if (isPrimary) {
    // Primary: citizenship required and tax_id validated per citizenship.
    return z
      .discriminatedUnion("citizenship", [
        z
          .object({
            citizenship: z.literal(INTERNATIONAL),
            tax_id: z
              .string()
              .trim()
              .min(1, { message: "TIN is required" })
              .max(20, { message: "TIN must be up to 20 characters" }),
          })
          .merge(schema),

        z
          .object({
            citizenship: z.literal(US_CITIZEN),
            tax_id: z.string().length(9, { message: "SSN must be 9 digits" }),
          })
          .merge(schema),
      ])
      .superRefine(
        postalCodeZodRefinement({
          countryField: "address_country_code",
          postalField: "address_postal_code",
          message: MESSAGE.REQUIRED("Postal Code"),
        }),
      );
  }

  // Additional signer: citizenship and tax_id optional.
  schema = schema.merge(
    z.object({
      citizenship: z.string().optional().nullable(),
      tax_id: z.string().optional().nullable(),
    }),
  );

  return isEntityNonPrimary ? uboRefine(schema) : schema;
};

export type IndividualSchema = ReturnType<typeof buildSchema>;
